"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.makeLegacyPlanVariables = makeLegacyPlanVariables;
exports.legacyPost = legacyPost;
exports.legacyPlanVarToPlanFunction = legacyPlanVarToPlanFunction;
exports.initLegacyFunctions = initLegacyFunctions;
exports.isLegacyPlan = isLegacyPlan;
const luxon_1 = require("luxon");
const common_1 = require("../../common/common");
const timeZones_1 = __importDefault(require("../../common/timeZones"));
const legacyPlanParser_1 = require("../parser/legacyPlanParser");
const legacyVariableDisplayNames_1 = __importDefault(require("./legacyVariableDisplayNames"));
const legacyVariableNames_1 = __importDefault(require("./legacyVariableNames"));
const planVariable_1 = require("../../model/v1/planVariable");
const planVariableTypes_1 = __importDefault(require("../../model/v1/planVariableTypes"));
const planFunction_1 = require("../../model/v1/planFunction");
// legacyPlan holds functions that for supporting legacy plan logic.
// These functions are all deprecated.
// Please see plan package for intended practice.
function makeLegacyPlanVariables(branchId) {
    const varsOut = [];
    // Revenue
    const revenueParams = {
        variableName: legacyVariableNames_1.default.REVENUE,
        id: 0,
        branchId: branchId,
        type: planVariableTypes_1.default.NUM,
    };
    const revOptions = {
        feId: legacyVariableNames_1.default.REVENUE,
        displayName: legacyVariableDisplayNames_1.default.REVENUE,
    };
    const revenue = new planVariable_1.PlanVariable(revenueParams, revOptions);
    varsOut.push(revenue);
    // Customer
    const customerParams = {
        variableName: legacyVariableNames_1.default.CUSTOMERS,
        id: 0,
        branchId: branchId,
        type: planVariableTypes_1.default.NUM,
    };
    const customerOptions = {
        feId: legacyVariableNames_1.default.CUSTOMERS, // NOTE: Must add branch id if multiple branchs' variables or values are put inside store.
        displayName: legacyVariableDisplayNames_1.default.CUSTOMERS,
    };
    const requiredStaff = new planVariable_1.PlanVariable(customerParams, customerOptions);
    varsOut.push(requiredStaff);
    // Memo
    const memoVarReq = {
        variableName: "",
        id: 0,
        branchId: branchId,
        type: planVariableTypes_1.default.STR,
    };
    const memoVarOpt = {
        feId: legacyVariableNames_1.default.MEMO,
        displayName: legacyVariableDisplayNames_1.default.MEMO,
    };
    const memoVar = new planVariable_1.PlanVariable(memoVarReq, memoVarOpt);
    varsOut.push(memoVar);
    return varsOut;
}
async function legacyPost(vals, userId, returnType, branchId) {
    let postType = "";
    const [firstValue] = vals;
    if (!firstValue) {
        return [];
    }
    switch (firstValue.getVariableId()) {
        case legacyVariableNames_1.default.CUSTOMERS:
            postType = "plans";
            break;
        case legacyVariableNames_1.default.REVENUE:
            postType = "revenue";
            returnType = legacyVariableNames_1.default.REVENUE;
            break;
        case legacyVariableNames_1.default.MEMO:
            postType = "memo";
            returnType = legacyVariableNames_1.default.MEMO;
            break;
        default:
            throw new Error('invalid type');
    }
    const plans = [];
    vals.forEach(v => {
        const d = {
            num: v.getNumValue()?.toString(),
            hour: "",
            revenue: "",
            memo: "",
            date: (0, common_1.yearMonthDateString)(v.getStartDate(), timeZones_1.default.ASIA_TOKYO),
        };
        switch (v.getVariableId()) {
            case legacyVariableNames_1.default.CUSTOMERS:
                let isHourly = false;
                if (v.getEndDate().valueOf() - v.getStartDate().valueOf() < 3600000) {
                    isHourly = true;
                }
                if (isHourly) {
                    d["hour"] = luxon_1.DateTime.fromJSDate(v.getStartDate()).setZone(timeZones_1.default.ASIA_TOKYO).hour.toString();
                }
                break;
            case legacyVariableNames_1.default.REVENUE:
                d.revenue = v.getNumValue()?.toString() || "";
                break;
            case legacyVariableNames_1.default.MEMO:
                d.memo = v.getStrValue();
                break;
            default:
                throw new Error('invalid type');
        }
        plans.push(d);
    });
    const params = new URLSearchParams({
        type: postType,
        plans: JSON.stringify(plans),
        c: branchId.toString(),
    });
    let out = [];
    const res = await fetch(`/dashboard/${userId}/plans?${params.toString()}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded"
        }
    });
    const json = await res.json();
    json.plans?.forEach(plan => {
        const values = (0, legacyPlanParser_1.parseValuesFromRawOplusTime)(plan);
        values.forEach(v => {
            if (!v.getVariableId().includes(returnType)) {
                return;
            }
            out.push(v);
        });
    });
    // NOTE: BE handler will also delete any plans that have empty values for all fields. Those will not be included in return array.
    // If FE needs to handle deletion case for some reason in the future, must check manually.
    return out;
}
function legacyPlanVarToPlanFunction(pv) {
    const req = {
        id: pv.getOriginalId(),
        branchId: pv.getOriginalBranchId(),
        variableName: pv.getVariableName(),
        type: pv.type,
        created: new Date(0),
    };
    const opt = {
        displayName: pv.getDisplayName(),
        feId: pv.getId(),
        allowDefaultFunction: false,
    };
    return new planFunction_1.PlanFunction(req, opt);
}
function initLegacyFunctions(functionStore, valueStore, planVarStore, params) {
    const vars = makeLegacyPlanVariables(params.branch.getOriginalId());
    vars.forEach(v => {
        planVarStore.add(v);
    });
    params.legacyPlans.forEach(l => {
        const values = (0, legacyPlanParser_1.parseValuesFromRawOplusTime)(l);
        values.forEach(v => {
            valueStore.add(v);
        });
    });
    // Convert legacy plan vars to functions
    const planVars = planVarStore.query({
        branchId: params.branch.getId(),
    });
    planVars.forEach(pv => {
        const pf = legacyPlanVarToPlanFunction(pv);
        functionStore.add(pf);
    });
    const req = {
        id: 0,
        variableName: "",
        branchId: params.branch.getOriginalId(),
        type: planVariableTypes_1.default.FUNC,
        created: new Date(0),
    };
    const opt = {
        feId: `${legacyVariableNames_1.default.CUSTOMERS}-${params.branch.getId()}-func`,
        allowDefaultFunction: true,
        functionBody: `{{${legacyVariableNames_1.default.CUSTOMERS}}} ${params.planEquationValue}`,
    };
    const customerEq = new planFunction_1.PlanFunction(req, opt);
    functionStore.add(customerEq);
}
function isLegacyPlan(id) {
    return legacyVariableNames_1.default.CUSTOMERS === id || legacyVariableNames_1.default.MEMO === id || legacyVariableNames_1.default.REVENUE === id;
}
